'use client';

import type { ReactNode } from 'react';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';

import { fNumber } from 'src/utils/format/format-number';
import { uniqueMentionsHint } from 'src/utils/brand/brand-quality-days';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { SegmentTabs } from 'src/components/cmui/segment-tabs';
import { HubStarButton } from 'src/components/cmui/hub/hub-star-button';
import { HubDetailToolbar } from 'src/components/cmui/hub/hub-detail-toolbar';
import { ShareLinkButton } from 'src/components/cmui/public-share/share-link-button';
import { HubRankBadge, HubEntityCard } from 'src/components/cmui/hub/hub-entity-card';

import { weekGrowthLabel } from '../utils/brand-hub-growth';
import { type BrandFace, BRAND_HUB_FACES } from '../constants/brand-hub-faces';
import { brandHubAdInfo, brandHubProductInfo } from '../utils/brand-hub-scope-note';
import { BrandHubConditionBar, type BrandHubConditionBarProps } from './brand-hub-condition-bar';

import type { BrandHubScopeOn } from '../utils/brand-hub-scope-note';
import type { BrandHubProductRegion } from '../utils/brand-hub-product-region';

/**
 * 브랜드 허브 상세의 **머리** — 조건 줄(`BrandHubDetailToolbar`)과 대상 카드·탭 줄(`BrandHubDetailHead`).
 *
 * 🔑 로그인한 상세와 공유 페이지(비회원)가 같은 카드·탭 줄을 쓴다 — 공유 페이지는 조건 줄을 세우지 않고 화면 전체를 누를 수 없게 막는다.
 */

/** 머리 조건 줄 — 「목록으로」가 칩 줄 왼쪽 끝에 선다(`HubDetailToolbar` · 크리에이터 허브와 같은 부품) */
export function BrandHubDetailToolbar({ onBack, ...bar }: BrandHubConditionBarProps & { onBack: () => void }) {
  return (
    <HubDetailToolbar onBack={onBack}>
      <BrandHubConditionBar {...bar} />
    </HubDetailToolbar>
  );
}

/** 대상 카드 오른쪽 줄 — 공유 · 즐겨찾기 별 · 추적 중 칩 또는 추적 단추 */
export function BrandHubHeadActions({
  onShare,
  saved,
  saving,
  onToggleSave,
  tracked,
  trackedLabel,
  trackedHref,
  onTrack,
  children,
}: {
  /** 공유 단추 — 비면 안 그린다(언급이 0 이면 공개 페이지가 404 다) */
  onShare?: () => void;
  saved: boolean;
  saving: boolean;
  onToggleSave: () => void;
  tracked: boolean;
  /** 「추적 중 · 세트이름」 */
  trackedLabel: string;
  /** 「추적 중」 칩이 가는 곳 — 그 추적 화면(브랜드 멘션 추적 또는 그 세트 화면) */
  trackedHref: string;
  onTrack: () => void;
  /** 뒤에 붙는 것 — 추적 등록 창 */
  children?: ReactNode;
}) {
  return (
    <>
      {/* 🔑 공유는 머리 한 자리다 — 소재 상세와 같은 단추·같은 말(「공유 링크를 복사했습니다.」) */}
      {onShare && <ShareLinkButton size="md" onClick={onShare} />}
      {/* 🔑 즐겨찾기는 별 하나다 — 글자가 붙은 단추는 추적 하나뿐이라 그 줄에서 할 일이 한눈에 갈린다 */}
      <HubStarButton saved={saved} saving={saving} disabled={saving} onToggle={onToggleSave} hint="즐겨찾기 — 목록에 표시만 합니다(수집은 돌지 않습니다)" />
      {/*
        🔑 「추적 중」 칩은 사실 표시다 — 옆의 「즐겨찾기」와 다른 이야기다. 누르면 그 추적 화면으로 간다.
      */}
      {tracked && (
        <Chip
          size="small"
          clickable
          component={RouterLink}
          href={trackedHref}
          icon={<Iconify icon="solar:bell-bing-linear" width={13} />}
          label={trackedLabel}
          sx={{ height: 28, borderRadius: RADIUS.pill, fontWeight: 600, bgcolor: 'primary.lighter', color: 'primary.dark', maxWidth: 220 }}
        />
      )}
      {/*
        🔑 누르면 **추적 등록 창**이 열리고 그 안에서 건다(`TrackRegisterDialog` · 칸 검사는 그 창의 공용 훅).
        ⛔ 글자에 이 화면의 건수를 적지 마라 — 추적은 앞으로 모으는 일이라 「N건 열고」는 거짓이다.
      */}
      {!tracked && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={onTrack}
          startIcon={<Iconify icon="solar:bell-bing-linear" width={15} />}
          sx={{ height: 28, borderRadius: RADIUS.pill, px: 2, typography: 'uiAction', boxShadow: 'none' }}
        >
          추적 등록
        </Button>
      )}
      {children}
    </>
  );
}

/** 대상 카드(요약 넷) + 품질 알림 한 줄(있을 때만) + 면 탭 줄 */
export function BrandHubDetailHead({
  name,
  imageUrl,
  periodLabel,
  head,
  scopeOn,
  productRegion,
  actions,
  face,
  onChangeFace,
  qualityNotice = '',
  rankLabel = '',
}: {
  name: string;
  imageUrl: string;
  periodLabel: string;
  head: {
    loading: boolean;
    total: number;
    recent7d: number;
    prev7d: number;
    topChannel: { name: string; count: number } | null;
    adTotal: number;
    productTotal: number;
    /** 반복 글을 한 건으로 센 기간 언급 — 있으면 기간 언급 칸의 보조 줄이 된다(`toUniqueMentions`) */
    uniqueMentions?: number | null;
  };
  scopeOn: BrandHubScopeOn;
  productRegion: BrandHubProductRegion;
  actions: ReactNode;
  face: BrandFace;
  onChangeFace: (next: BrandFace) => void;
  /**
   * 카드 밑 한 줄 — 같은 문구를 여러 계정이 반복 올린 브랜드면 그 몫(`brandQualityNotice`). 비면 안 선다.
   * ⚠️ 카드의 숫자는 그대로다 — 반복 글을 빼고 다시 세지 않는다.
   */
  qualityNotice?: string;
  /**
   * 이름 옆 순위 뱃지 — 「전체 브랜드」 목록의 `#` 과 같은 수(「12위」 · `brandHubRankLabel`).
   * 비면 안 그린다(받는 중 · 순위에서 뺀 브랜드 · 그 조건의 언급 없음).
   */
  rankLabel?: string;
}) {
  return (
    <>
      <HubEntityCard
        initial={name.slice(0, 1).toUpperCase()}
        imageUrl={imageUrl}
        logo
        title={name}
        badges={rankLabel ? <HubRankBadge label={rankLabel} /> : undefined}
        meta={`${periodLabel} · 이 브랜드가 언급된 글 기준`}
        actions={actions}
        stats={[
          {
            label: '최근 7일 언급',
            value: head.loading && head.recent7d === 0 ? null : fNumber(head.recent7d),
            hint: head.prev7d > 0 ? `지난주 대비 ${weekGrowthLabel(head.recent7d, head.prev7d)}` : '',
          },
          {
            label: `${periodLabel} 언급`,
            value: head.loading && head.total === 0 ? null : fNumber(head.total),
            hint: head.uniqueMentions != null ? uniqueMentionsHint(head.uniqueMentions) : head.topChannel ? `${head.topChannel.name}에서 가장 많이` : '',
          },
          // ⚠️ 광고·상품은 다른 원장이라 기간·채널(상품은 국가도)이 안 걸린다 — 걸린 조건은 ⓘ 에 적는다
          { label: '집행 중 광고', value: fNumber(head.adTotal), info: brandHubAdInfo(scopeOn) },
          { label: '판매몰 상품', value: fNumber(head.productTotal), info: brandHubProductInfo(scopeOn, productRegion) },
        ]}
      />
      {qualityNotice && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.5 }}>
          <Iconify icon="solar:info-circle-linear" width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
          <Typography sx={{ typography: 'uiMeta', color: 'text.secondary' }}>{qualityNotice}</Typography>
        </Stack>
      )}
      <SegmentTabs value={face} onChange={onChangeFace} tabs={BRAND_HUB_FACES} />
    </>
  );
}
